import { Field } from "@Light/components/system/Fieldset";
import { Select } from "@Light/components/system/Input";
import { monthName } from "@Light/utils/dateHelpers";
import dayjs from "dayjs";
import { useId, useState } from "react";
import type { UseFormRegisterReturn } from "react-hook-form";
import { FieldLabel } from "../FieldLabel";

const MIN_AGE = 18;
const MAX_AGE = 120;

export type BirthdayFieldsProps = {
  label: React.ReactNode;
  month: UseFormRegisterReturn;
  day: UseFormRegisterReturn;
  year: UseFormRegisterReturn;
  errors?: Partial<Record<"month" | "day" | "year", boolean>>;
};

export const BirthdayFields: React.FC<BirthdayFieldsProps> = ({
  label,
  month,
  day,
  year,
  errors,
}) => {
  const [latestYear] = useState(() => dayjs().year() - MIN_AGE);
  const idPrefix = useId();

  return (
    <Field className="flex w-full flex-col gap-1.5">
      <FieldLabel htmlFor={`${idPrefix}-month`}>{label}</FieldLabel>
      <div className="flex flex-row gap-4 *:min-w-0">
        <div className="flex-3">
          <Select
            id={`${idPrefix}-month`}
            aria-label="Month of birth"
            data-testid="birth-month-input"
            isError={errors?.month}
            {...month}
          >
            <option value="" disabled hidden>
              Month
            </option>
            {Array.from({ length: 12 }, (_, i) => {
              const value = String(i + 1).padStart(2, "0");
              return (
                <option key={value} value={value}>
                  {monthName(i + 1)}
                </option>
              );
            })}
          </Select>
        </div>
        <div className="flex-2">
          <Select
            id={`${idPrefix}-day`}
            aria-label="Day of birth"
            data-testid="birth-day-input"
            isError={errors?.day}
            {...day}
          >
            <option value="" disabled hidden>
              Day
            </option>
            {Array.from({ length: 31 }, (_, i) => {
              const value = String(i + 1).padStart(2, "0");
              return (
                <option key={value} value={value}>
                  {i + 1}
                </option>
              );
            })}
          </Select>
        </div>
        <div className="flex-2">
          <Select
            id={`${idPrefix}-year`}
            aria-label="Year of birth"
            data-testid="birth-year-input"
            isError={errors?.year}
            {...year}
          >
            <option value="" disabled hidden>
              Year
            </option>
            {Array.from({ length: MAX_AGE }, (_, i) => {
              const value = String(latestYear - i);
              return (
                <option key={value} value={value}>
                  {value}
                </option>
              );
            })}
          </Select>
        </div>
      </div>
    </Field>
  );
};
